import { useState, useEffect, useCallback, useRef } from 'react'
import { listProcessableCollections } from '../services/ingestion'
import type { ProcessableCollection } from '../types/ingestion'

interface UseProcessableCollectionsResult {
  collections: ProcessableCollection[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useProcessableCollections(caseId: string | null): UseProcessableCollectionsResult {
  const [collections, setCollections] = useState<ProcessableCollection[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Tracks which case the list has completed a first load for, so background
  // refreshes (polling) do not re-trigger the full-list loading placeholder.
  const loadedCaseRef = useRef<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setCollections([])
      setError(null)
      loadedCaseRef.current = null
      return
    }

    if (loadedCaseRef.current !== caseId) setLoading(true)
    setError(null)

    try {
      const data = await listProcessableCollections(caseId)
      setCollections(data)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to fetch processable collections')
      setCollections([])
    } finally {
      loadedCaseRef.current = caseId
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { collections, loading, error, refetch: fetch }
}
